import type {KeycloakTokenParsed} from 'keycloak-js';


/**
 * In-memory reference to the current Keycloak access token.
 * Undefined indicates that no access token is presently available (user unauthenticated
 * or initialization not yet completed). Token is never persisted (improves security vs localStorage).
 */
let _accessToken: string | undefined = undefined;


/**
 * In-memory reference to the parsed token payload (claims) provided by Keycloak.
 * Undefined indicates that no parsed token is available. The structure depends on the realm configuration.
 */
let _parsedToken: KeycloakTokenParsed | undefined = undefined;


/**
 * Updates the in-memory token store with a new access token and its parsed representation.
 * Passing undefined clears the current access token while still allowing a parsed payload update if provided.
 *
 * @param token The raw JWT access token string or undefined if it should be cleared.
 * @param parsed Optional parsed token payload (claims) returned by Keycloak.
 */
export function setToken(token: string | undefined, parsed?: KeycloakTokenParsed) {
  _accessToken = token;
  _parsedToken = parsed;
}


/**
 * Clears both the raw access token and its parsed payload from memory.
 * After invocation, the user is considered unauthenticated by helper checks.
 */
export function clearToken() {
  _accessToken = undefined;
  _parsedToken = undefined;
}


/**
 * Retrieves the current access token from memory.
 * @returns The raw JWT access token string if present; otherwise undefined.
 */
export function getAccessToken(): string | undefined {
  return _accessToken;
}


/**
 * Retrieves the parsed token payload (claims) from memory.
 * @returns The parsed token claims object if present; otherwise undefined.
 */
export function getParsedToken(): KeycloakTokenParsed | undefined {
  return _parsedToken;
}


/**
 * Indicates whether an access token is currently available in memory.
 * @returns true if an access token exists; false otherwise.
 */
export function isAuthenticated(): boolean {
  return _accessToken !== undefined;
}


/**
 * Checks whether the current parsed token grants a specified realm role.
 * Realm roles are found under `parsedToken.realm_access.roles`.
 *
 * @param role The realm role name to test for (exact match).
 * @returns true if the role is present and the token claims are loaded; false otherwise.
 */
export function hasRealmRole(role: string): boolean {
    const roles = _parsedToken?.realm_access?.roles || [];
    return roles.includes(role);
}
